import React from "react";
import {
  Columns,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
} from "lucide-react";
import type { BreakpointKey, BreakpointColumnConfig } from "../../types";
import { BREAKPOINTS } from "../../types";
import type { ColumnSettings } from "../UnifiedTableSettingsModal";

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

interface ColumnSettingsTabProps {
  columnSettings: ColumnSettings;
  onColumnSettingsChange: (settings: Partial<ColumnSettings>) => void;
  onColumnSelect: (columnIndex: number) => void;
  totalColumns: number;
  onApplyColumnSettings: () => void;
  onClose: () => void;
  onCloseWithoutReopen: () => void;
}

const defaultColumnConfig: Partial<BreakpointColumnConfig> = {
  widthType: "percent",
  widthValue: "33",
};

export const ColumnSettingsTab: React.FC<ColumnSettingsTabProps> = ({
  columnSettings,
  onColumnSettingsChange,
  onColumnSelect,
  totalColumns,
  onApplyColumnSettings,
  onClose,
  onCloseWithoutReopen,
}) => {
  // Helper to update column breakpoint config
  const updateColumnBreakpoint = (
    breakpoint: BreakpointKey,
    updates: Partial<BreakpointColumnConfig>
  ) => {
    const currentStyles = columnSettings.styles || {};
    const currentConfig = currentStyles[breakpoint] || defaultColumnConfig;
    onColumnSettingsChange({
      styles: {
        ...currentStyles,
        [breakpoint]: { ...currentConfig, ...updates },
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Column Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Column</label>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: totalColumns }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onColumnSelect(i)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                columnSettings.columnIndex === i
                  ? "bg-primary-main text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Column {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Per-breakpoint Column Settings */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Columns className="w-4 h-4" />
          Column {columnSettings.columnIndex + 1} Style (Per Breakpoint)
        </h4>
        
        <div className="space-y-3">
          {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
            const Icon = breakpointIcons[key];
            const bp = BREAKPOINTS[key];
            const config = columnSettings.styles?.[key] || defaultColumnConfig;
            
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                {/* Breakpoint Header & Width Control */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <Icon className="w-4 h-4 text-primary-main" />
                    <span className="font-semibold text-sm text-gray-800">{key.toUpperCase()}</span>
                    <span className="text-xs text-gray-500">({bp.description})</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-600">Width:</label>
                    <div className="flex gap-1">
                      {(["percent", "pixel"] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updateColumnBreakpoint(key, { widthType: type, widthValue: config.widthValue || (type === "percent" ? "33" : "200") })}
                          className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                            config.widthType === type
                              ? "bg-primary-main text-white"
                              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {type === "percent" ? "%" : "px"}
                        </button>
                      ))}
                    </div>
                      <input
                        type="number"
                        min="0"
                        placeholder={config.widthType === "percent" ? "%" : "px"}
                        value={config.widthValue}
                        onChange={(e) => updateColumnBreakpoint(key, { widthValue: e.target.value })}
                        className="w-20 border border-gray-300 px-2 py-1.5 rounded text-sm"
                      />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={() => {
            onCloseWithoutReopen();
            onApplyColumnSettings();
          }}
          className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90"
        >
          Apply to Column {columnSettings.columnIndex + 1}
        </button>
      </div>
    </div>
  );
};
